// ═══════════════════════════════════════════════════════════════════
// sculptedBody.jsx — THE GENERIC SCULPTED-BODY LOADER   (IE sculpt kit)
// ═══════════════════════════════════════════════════════════════════
// One factory for every being built with the sculpt kit. Proven by the
// Terror Plaza (willie / vyen / venus): toon ramp, inverted-hull ink,
// vertex-colour self-light patch, SkeletonUtils clone, error boundary +
// Suspense that fall back to the game's existing painted body.
//
//   const TigerSculpted = makeSculptedBody({
//     url: `${process.env.PUBLIC_URL || ""}/models/tiger.glb`, top: 1.2,   // Vite: import.meta.env.BASE_URL           // top = model height in glb units
//     noInk: /eyes|glow/,  glow: { tig_eyes: 0xffd24a },
//     tweak: (o) => { if (o.name === 'tig_gems') o.material.emissive.setHex(0x7a0818); },
//     pose: (B, t, ph, amt) => { ... },             // the game's adapter owns this
//     onMount: (S, ctl) => { ... },                  // once per mount, in a LAYOUT effect: rename bones
//                                                    // for a rig door, tell the host to re-fit (before paint)
//   });
//   <TigerSculpted ctl={ctl} h={1.2} Fallback={PaintedTiger} />
//
// ⚑ THEY OWN THE BODY, WE OWN THE POSE: the .glb is the master body;
//   `pose` is per-game and lives in the game's adapter, never in the glb.
// ⚑ Wrap it with sculptGate.jsx's failSafe() so a slow machine drops back
//   to the painted body for the rest of the visit.
// ⚑ <FrameWatch /> (sculptGate.jsx) must be mounted once in the <Canvas>.
// three ^0.185 (annihilation etc). Rush/tilt (^0.169): UNTESTED — check the
//   GLTFLoader/SkeletonUtils import paths and quantized loading first.
// ⚠ useLoader fetches when the component MOUNTS (visible or not): mount a
//   sculpted body only where it is actually shown.
// ═══════════════════════════════════════════════════════════════════
import { Component, Suspense, useEffect, useLayoutEffect, useMemo, useRef } from "react";
import { useFrame, useLoader } from "@react-three/fiber";
import * as THREE from "three";
import { GLTFLoader } from "three/examples/jsm/loaders/GLTFLoader.js";
import { clone as cloneSkinned } from "three/examples/jsm/utils/SkeletonUtils.js";

let RAMP = null;
export const toonRamp = () => {
  if (RAMP) return RAMP;
  RAMP = new THREE.DataTexture(new Uint8Array([172, 172, 172, 255, 228, 228, 228, 255, 255, 255, 255, 255]), 3, 1);
  RAMP.minFilter = RAMP.magFilter = THREE.NearestFilter;
  RAMP.needsUpdate = true;
  return RAMP;
};
const INKS = {};
export const inkMat = (width = 0.003, color = 0x161114) => {
  const k = width + ":" + color;
  if (INKS[k]) return INKS[k];
  const m = new THREE.MeshBasicMaterial({ color, side: THREE.BackSide });
  m.onBeforeCompile = (sh) => {
    sh.vertexShader = sh.vertexShader.replace("#include <begin_vertex>", `#include <begin_vertex>\n  transformed += normalize( normal ) * ${width.toFixed(4)};`);
  };
  // one program per width: three keys a custom shader on onBeforeCompile's
  // SOURCE TEXT, which is the same for every width
  m.customProgramCacheKey = () => "ie-ink-" + width.toFixed(4);
  return (INKS[k] = m);
};

export function prepareSculpt(scene, { noInk = /eyes/, glow = {}, selfLight = 0.3, ink = 0.003, tweak } = {}) {
  const root = cloneSkinned(scene);
  const meshes = [], glowing = {};
  root.traverse((o) => { if (o.isMesh) meshes.push(o); });
  meshes.forEach((o) => {
    const m = o.material;
    if (m.map) m.map.colorSpace = THREE.SRGBColorSpace;
    const vc = !!o.geometry.attributes.color;
    o.frustumCulled = false;
    if (glow[o.name] !== undefined) {
      o.material = new THREE.MeshBasicMaterial({ color: glow[o.name], map: m.map || null, vertexColors: vc, toneMapped: false });
      glowing[o.name] = o;
      return;
    }
    o.material = new THREE.MeshToonMaterial({
      color: m.color, map: m.map, gradientMap: toonRamp(), side: m.side, vertexColors: vc,
      emissive: m.color.clone().multiplyScalar(selfLight), emissiveMap: m.map || null,
    });
    // ⚠ vertex-coloured parts: scale the self-light by the vertex colour, or they wash grey
    if (vc) o.material.onBeforeCompile = (sh) => {
      sh.fragmentShader = sh.fragmentShader.replace("vec3 totalEmissiveRadiance = emissive;", "vec3 totalEmissiveRadiance = emissive * vColor.rgb;");
    };
    if (tweak) tweak(o);   // per-mesh touches: gem glow, dimmer hair, …
    if (noInk.test(o.name)) return;
    const h = o.isSkinnedMesh ? new THREE.SkinnedMesh(o.geometry, inkMat(ink)) : new THREE.Mesh(o.geometry, inkMat(ink));
    if (o.isSkinnedMesh) h.bind(o.skeleton, o.bindMatrix);
    h.name = o.name + "_ink"; h.frustumCulled = false;
    o.parent.add(h);
  });
  const B = {};
  root.traverse((o) => { if (o.isBone) { B[o.name] = o; o.userData.p0 = o.position.clone(); o.userData.y0 = o.position.y; } });   // y0: what the Plaza poses read
  return { root, B, glowing };
}

class GlbBoundary extends Component {
  constructor(p) { super(p); this.state = { bad: false }; }
  static getDerivedStateFromError() { return { bad: true }; }
  componentDidCatch(e) { console.warn(`[sculpt] ${this.props.url} did not load — the painted body stands in`, e); }
  render() { return this.state.bad ? this.props.fallback : this.props.children; }
}

export function makeSculptedBody({ url, top = 1, pose, tick, onMount, ...prep }) {
  function Model({ ctl, h = top }) {
    const gltf = useLoader(GLTFLoader, url);
    const S = useMemo(() => prepareSculpt(gltf.scene, prep), [gltf]); // eslint-disable-line react-hooks/exhaustive-deps
    // once per mount, before paint (a host re-fits in the same commit)
    useLayoutEffect(() => { if (onMount) onMount(S, ctl); }, [S]); // eslint-disable-line react-hooks/exhaustive-deps
    // this mount's materials are its own (the ink is shared and cached; textures belong to the loader cache)
    useEffect(() => () => S.root.traverse((o) => {
      if (o.isMesh && o.material && !/_ink$/.test(o.name)) o.material.dispose();
    }), [S]);
    const g = useRef(); const yaw = useRef(0); const ph = useRef(0); const amt = useRef(0);
    useFrame(({ clock }, dtRaw) => {
      const o = g.current; if (!o) return;
      const dt = Math.min(dtRaw || 0.016, 0.05); const t = clock.getElapsedTime();
      const f = (ctl && ctl.facing) || { x: 0, y: 1 };
      const want = Math.atan2(f.x || 0, f.y === undefined ? 1 : f.y);
      let d = want - yaw.current; while (d > Math.PI) d -= Math.PI * 2; while (d < -Math.PI) d += Math.PI * 2;
      yaw.current += d * Math.min(1, dt * 9);
      o.rotation.y = yaw.current;
      const speed = (ctl && ctl.speed) || 0;
      amt.current += ((speed ? 1 : 0) - amt.current) * Math.min(1, dt * 6);
      ph.current += (speed ? 1 : amt.current) * dt * 7;
      if (pose) pose(S.B, t, ph.current, amt.current, ctl);
      if (tick) tick(S, t, dt, ctl);
    }, -1);   // FIRST in the frame (negative priority does not take over rendering): the pose is the
              // base, and a host adding strike/kick deltas onto the same bones must write AFTER it
    return <group ref={g}><primitive object={S.root} scale={h / top} /></group>;
  }
  // `rest` goes to the painted Fallback only; the sculpted body gets game data through `ctl`
  function Sculpted({ ctl, h, Fallback, ...rest }) {
    const fb = Fallback ? <Fallback ctl={ctl} h={h} {...rest} /> : null;
    return (
      <GlbBoundary url={url} fallback={fb}>
        <Suspense fallback={fb}><Model ctl={ctl} h={h} /></Suspense>
      </GlbBoundary>
    );
  }
  Sculpted.Model = Model;
  return Sculpted;
}
